import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin } from "lucide-react";
import { Reveal } from "./Reveal";
import { BRAND, IMAGES } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in every field.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Thank you — your message is on its way. I'll be in touch soon.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or email me directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    borderBottom: "1px solid var(--border-light)",
    color: "var(--text-primary)",
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-36"
      style={{ backgroundColor: "var(--bg-secondary)" }}
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20 items-start">
        <div className="lg:col-span-5">
          <Reveal>
            <p
              className="text-sm tracking-[0.28em] uppercase mb-5"
              style={{ color: "var(--rose-mocha-deep)" }}
            >
              Let's Talk
            </p>
            <h2
              className="font-serif text-4xl md:text-5xl leading-tight tracking-tight mb-8"
              style={{ color: "var(--text-primary)" }}
            >
              Ready to reclaim your time?
            </h2>
            <p
              className="text-base md:text-lg leading-relaxed mb-10 max-w-md"
              style={{ color: "var(--text-secondary)" }}
            >
              Tell me a little about what's on your plate. I'll reply with a
              tailored recommendation and next steps.
            </p>

            <ul className="flex flex-col gap-5 mb-10">
              {[
                { Icon: Phone, text: BRAND.phone },
                { Icon: Mail, text: BRAND.email, href: `mailto:${BRAND.email}` },
                { Icon: MapPin, text: BRAND.location },
              ].map(({ Icon, text, href }) => (
                <li key={text} className="flex items-center gap-4">
                  <span
                    className="grid place-items-center w-11 h-11 rounded-full shrink-0"
                    style={{ backgroundColor: "var(--rose-mocha)" }}
                  >
                    <Icon size={18} color="#FBF6F3" />
                  </span>
                  {href ? (
                    <a
                      href={href}
                      className="link-underline text-base md:text-lg break-all"
                      style={{ color: "var(--text-primary)" }}
                    >
                      {text}
                    </a>
                  ) : (
                    <span className="text-base md:text-lg" style={{ color: "var(--text-primary)" }}>
                      {text}
                    </span>
                  )}
                </li>
              ))}
            </ul>

            <div
              className="img-zoom-wrap rounded-2xl overflow-hidden h-56 hidden lg:block"
              style={{ border: "1px solid var(--border-light)" }}
            >
              <img
                src={IMAGES.contactAccent}
                alt="Warm minimal coffee scene"
                className="w-full h-full object-cover"
              />
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-6 lg:col-start-7">
          <Reveal delay={0.15}>
            <form onSubmit={submit} data-testid="contact-form" className="flex flex-col gap-8">
              <div className="flex flex-col gap-2">
                <label
                  className="text-xs tracking-[0.2em] uppercase"
                  style={{ color: "var(--rose-mocha-deep)" }}
                >
                  Your name
                </label>
                <input
                  data-testid="contact-name-input"
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Jane Doe"
                  className="bg-transparent py-3 outline-none text-lg placeholder:opacity-40"
                  style={inputStyle}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label
                  className="text-xs tracking-[0.2em] uppercase"
                  style={{ color: "var(--rose-mocha-deep)" }}
                >
                  Email address
                </label>
                <input
                  data-testid="contact-email-input"
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="you@company.com"
                  className="bg-transparent py-3 outline-none text-lg placeholder:opacity-40"
                  style={inputStyle}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label
                  className="text-xs tracking-[0.2em] uppercase"
                  style={{ color: "var(--rose-mocha-deep)" }}
                >
                  How can I help?
                </label>
                <textarea
                  data-testid="contact-message-input"
                  value={form.message}
                  onChange={set("message")}
                  rows={4}
                  placeholder="Tell me about your business and what you'd like to delegate…"
                  className="bg-transparent py-3 outline-none text-lg resize-none placeholder:opacity-40"
                  style={inputStyle}
                />
              </div>
              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={loading}
                className="self-start inline-flex items-center rounded-full px-8 py-4 text-sm font-medium transition-opacity duration-300 disabled:opacity-60"
                style={{ backgroundColor: "var(--mocha-brown)", color: "#FBF6F3" }}
              >
                {loading ? "Sending…" : "Send message"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
